import styled from '@emotion/styled'
import { AdUnitLevelDimensions, AllDimensions, Dimension, MobileInventoryType } from '@pubstack/common/src/analytics/dimension'
import { AnalyticsQueryDashboard, AnalyticsQueryDashboardName, GamOverviewMetricsByTime, MappedName } from '@pubstack/common/src/analytics/query'
import { sameDay } from '@pubstack/common/src/date'
import { useAtom } from 'jotai'
import { DateTime } from 'luxon'
import { FunctionComponent, useState } from 'react'
import { useAnalyticsQueryTanstack } from '~/api/analytics/analytics-query.api'
import { LazyVisible } from '~/components/LazyVisible'
import { Link, LinkIcon } from '~/components/Link'
import { normalizeAsTrackingLabel, TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { SelectableOptionsPopover, SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useScopeCurrency } from '~/hooks/useScopeCurrency.hooks'
import { AnalyticsPage } from '~/modules/analytics/AnalyticsPage'
import { videoCompletionRate, videoViewershipTotalErrorCount } from '~/modules/analytics/common/formulas'
import { useBuildContextWithFilterEnforced } from '~/modules/analytics/useContext'
import { contextState } from '~/state'
import { WithClassName } from '~/types/utils'
import { getFilterSidebarContent, onBreakdownRowClick } from '~/utils/analytics'
import { GamOverviewDimensions } from '~/utils/gamOverview'
import { PureGamOverviewBreakdown } from './PureGamOverviewBreakdown'
import { PureGamOverviewTimeline } from './PureGamOverviewTimeline'
import { PureGamOverwiewWidgetFunnel } from './PureGamOverviewWidgetFunnel'

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`
const SortSelect = styled(SelectableOptionsPopover)`
  margin-left: 4px;
`
const SortLink = styled(Link)<{ open: boolean }>`
  ${LinkIcon} {
    transition: 0.25s;
    transform: rotate(${(props) => (props.open ? '-180deg' : '0')});
  }
`

const Wrapper = styled.div`
  display: flex;
  align-items: center;
`

const DimensionSelector: FunctionComponent<{ options: SelectOptionProp<Dimension>[]; currentDimension: string; onChange: (option: SelectOptionProp) => unknown }> = ({
  options,
  currentDimension,
  onChange,
}) => {
  const [sortOpen, setSortOpen] = useState<boolean>(false)

  const adUnitPathOption: SelectOptionProp<SelectOptionProp<string>[]> = { label: 'Ad unit path', value: [], trackingLabel: 'ana-gam-overview-dimension-ad-unit-path-set' }
  const SELECTOR_NAME_CONFIG: { [key in (typeof AdUnitLevelDimensions)[number]]: string } = {
    adUnitLevel1: 'Level 1',
    adUnitLevel2: 'Level 2',
    adUnitLevel3: 'Level 3',
    adUnitLevel4: 'Level 4',
    adUnitLevel5: 'Level 5',
  }

  const refinedOptions = options.reduce<SelectOptionProp[]>((acc, option) => {
    if (Object.keys(SELECTOR_NAME_CONFIG).includes(option.value)) {
      if (!acc.find((option) => option.label === adUnitPathOption.label)) {
        acc.push(adUnitPathOption)
      }
      adUnitPathOption.value.push({
        ...option,
        label: SELECTOR_NAME_CONFIG[option.value as (typeof AdUnitLevelDimensions)[number]],
      })
    } else {
      acc.push(option)
    }
    return acc
  }, [])

  return (
    <Wrapper>
      By :
      <SortSelect
        options={refinedOptions}
        onChange={onChange}
        trigger={<SortLink open={sortOpen} label={currentDimension} icon={'chevron_down'} trackingLabel={`ana-gam-overview-dimension-change-apply`} />}
        open={sortOpen}
        setOpen={setSortOpen}
      />
    </Wrapper>
  )
}

type GamOverviewPageProps = WithClassName & {
  inventoryType: MobileInventoryType
}

const BaseGamOverviewPage: FunctionComponent<GamOverviewPageProps> = ({ className, inventoryType }) => {
  const today = DateTime.now().startOf('day')
  const webOnlyDimensions: Dimension[] = ['adUnit', 'tagId']
  const selectorDimensions: Dimension[] = [
    'device',
    'pubstackDemandChannel',
    'abTestPopulation',
    'adUnitLevel1',
    'adUnitLevel2',
    'adUnitLevel3',
    'adUnitLevel4',
    'adUnitLevel5',
    ...(inventoryType === 'Web' ? webOnlyDimensions : []),
  ]
  const dimensions: Dimension[] = [...selectorDimensions, 'mediaType', 'country']
  const [context, setContext] = useAtom(contextState)
  const [currentDimension, setCurrentDimension] = useState<GamOverviewDimensions>('pubstackDemandChannel')
  const dashboard: AnalyticsQueryDashboard = { name: `gam-overview-${inventoryType.toLowerCase()}` as AnalyticsQueryDashboardName, filterType: 'revenues' }
  const currencySymbol = useScopeCurrency()
  const queryDim: Dimension[] = [...dimensions, 'pubstackInventoryType']
  const buildContext = useBuildContextWithFilterEnforced('pubstackInventoryType', { label: 'pubstackInventoryType', value: inventoryType })
  const analyticsQueryTime = useAnalyticsQueryTanstack<GamOverviewMetricsByTime>('gam-overview.metrics.by.time', queryDim, buildContext(context), { dimension: currentDimension, inventoryType })

  const timelineEmptyData: GamOverviewMetricsByTime = {
    epoch: [],
    impressionCpmSum: [],
    impressionCount: [],
    measurableImpressionCount: [],
    mrcViewableImpressionCount: [],
    videoViewershipComplete: [],
    videoErrorCount: [],
    videoStart: [],
    videoFirstQuartile: [],
    videoMidpoint: [],
    videoThirdQuartile: [],
    mappings: {},
  }
  const isTodaySelected = sameDay(context.timeRange.from, today) && sameDay(context.timeRange.to, today)

  const dimensionSelectorOptions: SelectOptionProp<(typeof AllDimensions)[number]>[] = getFilterSidebarContent() // TODO 2022-09-01 NRA VMA retrieve options from Dimensions/GamOverviewDimensions => need key/value object for displayable dimension label
    .filter((fs) => selectorDimensions.includes(fs.dimension))
    .map((d) => ({ label: d.title, value: d.dimension, trackingLabel: `ana-gam-overview-dimension-${normalizeAsTrackingLabel(d.dimension)}-set` }) as SelectOptionProp<(typeof AllDimensions)[number]>)
    .sort((a, b) => {
      if (a.value === 'pubstackDemandChannel') {
        return -1
      }
      if (b.value === 'pubstackDemandChannel') {
        return 1
      }
      return 0
    })

  const onSwitchTo7DaysClick = () => {
    setContext({
      ...context,
      timeRange: {
        from: today.minus({ days: 7 }),
        to: today.minus({ days: 1 }),
        tz: context.timeRange.tz,
      },
    })
  }

  const onRowClick = (name: MappedName) => {
    // 2022-10-27 LES BVI : We don't want the "Others" to be clickable in the breakdown (we accept the Others in devices because it is a real aggregation from backend)
    if (currentDimension === 'device' || name.value !== 'Others') {
      onBreakdownRowClick(currentDimension, setContext)(name)
    }
  }

  const hasVideoInstream = (data: GamOverviewMetricsByTime): boolean => videoCompletionRate.sum(data) !== 0 || videoViewershipTotalErrorCount.sum(data) !== 0

  const currentDimensionLabel = dimensionSelectorOptions.find((d) => d.value === currentDimension)?.label || ''

  const generateDisableTodayFilterParameter = (trackingLabel: TrackingLabel) => {
    return isTodaySelected
      ? {
          switchTo7DaysTrackingLabel: trackingLabel,
          onSwitchTo7DaysClick,
        }
      : undefined
  }

  const breadcrumbs = useBreadcrumbs()
  return (
    <AnalyticsPage
      className={className}
      filterDimensions={dimensions}
      dashboard={dashboard}
      queryDimensions={queryDim}
      buildContext={buildContext}
      title={
        <>
          {breadcrumbs}
          <DimensionSelector currentDimension={currentDimensionLabel} options={dimensionSelectorOptions} onChange={(dimension) => setCurrentDimension(dimension.value as GamOverviewDimensions)} />
        </>
      }
    >
      <PageContent>
        <PureGamOverviewTimeline
          dimension={currentDimension}
          currencySymbol={currencySymbol}
          rawData={analyticsQueryTime.data ?? timelineEmptyData}
          isLoading={analyticsQueryTime.isFetching}
          error={analyticsQueryTime.isError}
          empty={!analyticsQueryTime.isFetching && !analyticsQueryTime?.data?.epoch.length}
          disableTodayFilter={generateDisableTodayFilterParameter('ana-gam-overview-timeline-filter-seven-days-apply')}
          onRefreshClick={() => {
            analyticsQueryTime.refetch()
          }}
          onMetricChange={() => {}}
          displayVideoData={hasVideoInstream(analyticsQueryTime.data ?? timelineEmptyData)}
        />
        <LazyVisible minHeight={500}>
          <PureGamOverviewBreakdown
            currencySymbol={currencySymbol}
            rawData={analyticsQueryTime.data ?? timelineEmptyData}
            isLoading={analyticsQueryTime.isFetching}
            error={analyticsQueryTime.isError}
            empty={!analyticsQueryTime.isFetching && !analyticsQueryTime?.data?.epoch.length}
            disableTodayFilter={generateDisableTodayFilterParameter('ana-gam-overview-breakdown-filter-seven-days-apply')}
            onRefreshClick={() => {
              analyticsQueryTime.refetch()
            }}
            onRowClick={onRowClick}
            onSortChange={() => {}}
            dimension={currentDimension}
            displayVideoData={hasVideoInstream(analyticsQueryTime.data ?? timelineEmptyData)}
          />
        </LazyVisible>
        <LazyVisible minHeight={400}>
          <PureGamOverwiewWidgetFunnel
            data={analyticsQueryTime.data ?? timelineEmptyData}
            isLoading={analyticsQueryTime.isFetching}
            error={analyticsQueryTime.isError}
            empty={!analyticsQueryTime.isFetching && (!analyticsQueryTime?.data?.epoch.length || !hasVideoInstream(analyticsQueryTime.data ?? timelineEmptyData))}
            disableTodayFilter={generateDisableTodayFilterParameter('ana-gam-overview-funnel-filter-seven-days-apply')}
            onRefreshClick={() => {
              analyticsQueryTime.refetch()
            }}
          />
        </LazyVisible>
      </PageContent>
    </AnalyticsPage>
  )
}

export const GamOverviewPage = styled(BaseGamOverviewPage)``
